import { createSelector } from 'reselect';
// Import enumerations.
import {
	EndorsementEligibilityStatusType,
} from '@app/enumerations';
// Import state types.
import {
	AppState,
	PolicyState,
} from '@app/redux/states';
// Import props.
import {
	OptionalPolicyProps,
	PolicyProps,
} from '@app/props';
// Import api models.
import {
	EndorsementEligibilityStatus,
	ParentProducerAgency,
	ProducerAgency,
	ProducerAgent,
	ProducerInfo,
} from '@app/models/api';
// Import app models.
import {
	FormAdvisoryMessage
} from '@app/models/app';

// Fetches the policy state.
const getPoliciesState = (state: AppState, props: PolicyProps | OptionalPolicyProps): PolicyState | undefined => {
	if (props.policyId !== undefined) {
		return state.policies[props.policyId];
	}
	return undefined;
}

// Get Policy Term Years Selector.
const makeGetPolicyTermYearsSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): number[] => {
		return policyState !== undefined
			? policyState.policyTermYears
			: [];
	}
);

// Get Policy Producer Selector.
const makeGetPolicyProducerSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): ProducerInfo | undefined =>
		policyState?.policyProducer ?? undefined
);

// Get Policy Producer Agent Selector.
const makeGetPolicyProducerAgentSelector = () => createSelector(
	[makeGetPolicyProducerSelector()],
	(policyProducer: ProducerInfo | undefined): ProducerAgent | undefined => 
		policyProducer?.producerAgent ?? undefined	
);

// Get Policy Producer Agent Id Selector.
const makeGetPolicyProducerAgentIdSelector = () => createSelector(
	[makeGetPolicyProducerAgentSelector()],
	(producerAgent: ProducerAgent | undefined): number | undefined => 
		producerAgent?.agentId ?? undefined
);

// Get Policy Producer Agent Name Selector.
const makeGetPolicyProducerAgentNameSelector = () => createSelector(
	[makeGetPolicyProducerAgentSelector()],
	(producerAgent: ProducerAgent | undefined): string => 
		producerAgent?.name ?? 'Anonymous'
);

// Get Policy Producer Agent Email Selector.
const makeGetPolicyProducerAgentEmailSelector = () => createSelector(
	[makeGetPolicyProducerAgentSelector()],
	(producerAgent: ProducerAgent | undefined): string => 
		producerAgent?.email ?? 'No Email Provided'
);

// Get Policy Producer Agency Selector.
const makeGetPolicyProducerAgencySelector = () => createSelector(
	[makeGetPolicyProducerSelector()],
	(policyProducer: ProducerInfo | undefined): ProducerAgency | undefined =>
		policyProducer?.producerAgency ?? undefined
);

// Get Policy Producer Agency Id Selector.
const makeGetPolicyProducerAgencyIdSelector = () => createSelector(
	[makeGetPolicyProducerAgencySelector()],
	(producerAgency: ProducerAgency | undefined): number | undefined =>
		producerAgency?.agencyId ?? undefined
);

// Get Policy Producer Agency Name Selector.
const makeGetPolicyProducerAgencyNameSelector = () => createSelector(
	[makeGetPolicyProducerAgencySelector()],
	(producerAgency: ProducerAgency | undefined): string => 
		producerAgency?.name ?? 'Anonymous'
);

// Get Policy Producer Agency Number Selector.
const makeGetPolicyProducerAgencyNumberSelector = () => createSelector(
	[makeGetPolicyProducerAgencySelector()],
	(producerAgency: ProducerAgency | undefined): string =>
		producerAgency?.phone ?? 'No Number Provided'
);

// Get Policy Parent Producer Agency Selector.
const makeGetPolicyParentProducerAgencySelector = () => createSelector(
	[makeGetPolicyProducerSelector()],
	(policyProducer: ProducerInfo | undefined): ParentProducerAgency | undefined =>
		policyProducer?.parentProducerAgency ?? undefined
);

// Get Policy Parent Producer Agency Id Selector.
const makeGetPolicyParentProducerAgencyIdSelector = () => createSelector(
	[makeGetPolicyParentProducerAgencySelector()],
	(parentProducerAgency: ParentProducerAgency | undefined): number | undefined =>
		parentProducerAgency?.agencyId ?? undefined
);

// Get Policy Parent Producer Agency Name Selector.
const makeGetPolicyParentProducerAgencyNameSelector = () => createSelector(
	[makeGetPolicyParentProducerAgencySelector()],
	(parentProducerAgency: ParentProducerAgency | undefined): string | undefined =>
		parentProducerAgency?.name ?? undefined
);

// Get Policy Parent Producer Agency Number Selector.
const makeGetPolicyParentProducerAgencyNumberSelector = () => createSelector(
	[makeGetPolicyParentProducerAgencySelector()],
	(parentProducerAgency: ParentProducerAgency | undefined): string | undefined =>
		parentProducerAgency?.phone ?? undefined
);

// Is Policy Parent Producer Agency Overridden Selector.
const makeIsPolicyParentProducerAgencyOverriddenSelector = () => createSelector(
	[makeGetPolicyParentProducerAgencySelector()],
	(parentProducerAgency: ParentProducerAgency | undefined): boolean =>
		parentProducerAgency?.isOverridden ?? false
);

// Are Policy Term years Loaded Selector.
const makeArePolicyTermYearsLoadedSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): boolean => {
		return policyState !== undefined
			? policyState.arePolicyTermYearsLoaded
			: false;
	}
);

// Is Producer Loaded Selector.
const makeIsProducerLoadedSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): boolean => {
		return policyState !== undefined
			? policyState.isProducerLoaded
			: false;
	}
);

// Is Policy Copy Eligiblity Status Loaded Selector.
const makeIsPolicyCopyEligiblityStatusLoadedSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): boolean => {
		return policyState?.canPolicyBeCopied !== undefined
	}
);

// Can Policy Be Copied Selector.
const makeCanPolicyBeCopiedSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): boolean => {
		return policyState?.canPolicyBeCopied ?? false;
	}
);

// Get Endorsement Eligibility Status Selector.
const makeGetEndorsementEligibilityStatusSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): EndorsementEligibilityStatus | undefined => {
		return policyState?.endorsementEligibilityStatus ?? undefined;
	}
);

// Get Endorsement Eligibility Status Type Selector.
const makeGetEndorsementEligibilityStatusTypeSelector = () => createSelector(
	[makeGetEndorsementEligibilityStatusSelector()],
	(endorsementEligibilityStatus: EndorsementEligibilityStatus | undefined): EndorsementEligibilityStatusType | undefined => {
		return endorsementEligibilityStatus?.status ?? undefined;
	}
);

// Get Endorsement Eligibility Status Endorsement Id Selector.
const makeGetEndorsementEligibilityStatusEndorsementIdSelector = () => createSelector(
	[makeGetEndorsementEligibilityStatusSelector()],
	(endorsementEligibilityStatus: EndorsementEligibilityStatus | undefined): number | undefined => {
		return endorsementEligibilityStatus?.endorsementId ?? undefined;
	}
);

// Can Start New Endorsement Selector.
const makeCanStartNewEndorsementSelector = () => createSelector(
	[makeGetEndorsementEligibilityStatusTypeSelector()],
	(endorsementEligibilityStatusType: EndorsementEligibilityStatusType | undefined): boolean => {
		return endorsementEligibilityStatusType === EndorsementEligibilityStatusType.ELIGIBLE;
	}
);

// Get Endorsement Eligibility Status Messages Selector.
const makeGetEndorsementEligibilityStatusMessagesSelector = () => createSelector(
	[makeGetEndorsementEligibilityStatusTypeSelector()],
	(endorsementEligibilityStatusType: EndorsementEligibilityStatusType | undefined): FormAdvisoryMessage[] => {
		let messages: FormAdvisoryMessage[] = [];
		switch (endorsementEligibilityStatusType) {
			case EndorsementEligibilityStatusType.ENDORSEMENT_IN_PROGRESS:
				messages = [
					new FormAdvisoryMessage({
						message: 'Please complete or remove your endorsement in the works by clicking the View Pending Endorsement button below.',
						bulletinPoints: [],
					}),
					new FormAdvisoryMessage({
						message: 'Still have questions?  Give us a call +1 (801) 316-4228.',
						bulletinPoints: [],
					}),
				];
				break;
			case EndorsementEligibilityStatusType.ENDORSEMENT_SUBMITTED:
				messages = [
					new FormAdvisoryMessage({
						message: 'An endorsement was previously submitted to underwriting for consideration.  You may unsubmit the pending change by clicking the View Pending Endorsement button below or wait for the Underwriter to make a decision.  Most requests are reviewed within a few hours.',
						bulletinPoints: [],
					}),
					new FormAdvisoryMessage({
						message: 'Still have questions?  Give us a call +1 (801) 316-4228.',
						bulletinPoints: [],
					}),
				];
				break;
			case EndorsementEligibilityStatusType.INACTIVE_POLICY:
				messages = [
					new FormAdvisoryMessage({
						message: 'This policy is no longer in force.  Please click below to go back to the policy summary.  There you can review the cancellation or copy information over to a new quote.',
						bulletinPoints: [],
					}),
					new FormAdvisoryMessage({
						message: 'Still have questions?  Give us a call +1 (801) 316-4228.',
						bulletinPoints: [],
					}),
				];
				break;
			case EndorsementEligibilityStatusType.ENDORSEMENT_APPROVED_BUT_UNPAID:
				messages = [
					new FormAdvisoryMessage({
						message: 'This policy has an endorsement waiting for payment.  Before additional changes can be made you need to pay for the endorsement or un-submit the pending endorsement. To view the endorsement, click below.  Once there you can un-submit the endorsement and make other changes. ',
						bulletinPoints: [],
					}),
					new FormAdvisoryMessage({
						message: 'Still have questions?  Give us a call +1 (801) 316-4228.',
						bulletinPoints: [],
					}),
				];
				break;
			case EndorsementEligibilityStatusType.RENEWAL_PERIOD:
				messages = [
					new FormAdvisoryMessage({
						message: 'We\'ve already sent out the renewal notice.  To avoid billing mistakes we do not allow any \"Premium Bearing Endorsements\" after the renewal billings has gone out (typically 65 days before the policy expires).  You can make changes to the renewal policy once the premium is paid.',
						bulletinPoints: [],
					}),
					new FormAdvisoryMessage({
						message: 'Still have questions?  Give us a call +1 (801) 316-4228.',
						bulletinPoints: [],
					}),
				];
				break;
			case EndorsementEligibilityStatusType.ELIGIBLE:
				messages = [
					new FormAdvisoryMessage({
						message: String.empty(),
						bulletinPoints: [
							'Any endorsements that are still in the works or pending underwriting review will be cancelled when the renewal period starts.',
							'Any endorsements that are approved but not acted on will be applied towards the next term when the renewal period starts.'
						],
					}),
					new FormAdvisoryMessage({
						message: 'Do you wish to continue?',
						bulletinPoints: [],
					}),
				];
				break;
		}
		return messages;
	}
);

// Get Endorsement Eligibility Status Title Selector.
const makeGetEndorsementEligibilityStatusTitleSelector = () => createSelector(
	[makeGetEndorsementEligibilityStatusTypeSelector()],
	(endorsementEligibilityStatusType: EndorsementEligibilityStatusType | undefined): string => {
		let title: string = String.empty();
		switch (endorsementEligibilityStatusType) {
			case EndorsementEligibilityStatusType.ENDORSEMENT_IN_PROGRESS:
			case EndorsementEligibilityStatusType.ENDORSEMENT_SUBMITTED:
			case EndorsementEligibilityStatusType.INACTIVE_POLICY:
			case EndorsementEligibilityStatusType.ENDORSEMENT_APPROVED_BUT_UNPAID:
				title = 'Oops! You are unable to view this page';
				break;
			case EndorsementEligibilityStatusType.RENEWAL_PERIOD:
				title = 'Oops! Bad Timing';
				break;
			case EndorsementEligibilityStatusType.ELIGIBLE:
				title = 'Before starting an endorsement, please be aware of the following:'
				break;
		}
		return title;
	}
);

// Export composed methods.
export {
	getPoliciesState,
};

// export selectors.
export {
	makeGetPolicyTermYearsSelector,
	makeGetPolicyProducerSelector,
	makeGetPolicyProducerAgentSelector,
	makeGetPolicyProducerAgentIdSelector,
	makeGetPolicyProducerAgentNameSelector,
	makeGetPolicyProducerAgentEmailSelector,
	makeGetPolicyProducerAgencySelector,
	makeGetPolicyProducerAgencyIdSelector,
	makeGetPolicyProducerAgencyNameSelector,
	makeGetPolicyProducerAgencyNumberSelector,
	makeGetPolicyParentProducerAgencySelector,
	makeGetPolicyParentProducerAgencyIdSelector,
	makeGetPolicyParentProducerAgencyNameSelector,
	makeGetPolicyParentProducerAgencyNumberSelector,
	makeIsPolicyParentProducerAgencyOverriddenSelector,
	makeArePolicyTermYearsLoadedSelector,
	makeIsProducerLoadedSelector,
	makeIsPolicyCopyEligiblityStatusLoadedSelector,
	makeCanPolicyBeCopiedSelector,
	makeGetEndorsementEligibilityStatusSelector,
	makeGetEndorsementEligibilityStatusTypeSelector,
	makeGetEndorsementEligibilityStatusEndorsementIdSelector,
	makeCanStartNewEndorsementSelector,
	makeGetEndorsementEligibilityStatusMessagesSelector,
	makeGetEndorsementEligibilityStatusTitleSelector,
};